"use client";

import { useState } from "react";

import { rememberCheckout } from "@/lib/analytics/checkout";
import { track, type AnalyticsParams } from "@/lib/analytics/track";
import type { CheckoutSelection } from "@/lib/stripe/selection";

/**
 * Starts Checkout. The browser only sends the selection and the amount it
 * showed. Price IDs stay on the server.
 */
export default function CheckoutPayButton({
  label,
  request,
  className,
  analyticsParams,
}: {
  label: string;
  request: CheckoutSelection;
  className?: string;
  analyticsParams?: AnalyticsParams;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function startCheckout() {
    if (pending) return;
    if (analyticsParams) track("checkout_click", analyticsParams);
    setPending(true);
    setError(null);
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      const data = (await response.json()) as { url?: unknown };
      if (!response.ok || typeof data.url !== "string" || data.url.length === 0) {
        setError("Checkout is unavailable right now. Send details and I'll follow up.");
        setPending(false);
        return;
      }
      if (analyticsParams) rememberCheckout(analyticsParams);
      window.location.assign(data.url);
    } catch {
      setError("Checkout is unavailable right now. Send details and I'll follow up.");
      setPending(false);
    }
  }

  return (
    <>
      <button className={className} type="button" onClick={startCheckout} disabled={pending}>
        {pending ? "Redirecting to checkout…" : label}
      </button>
      {error ? <span className="caption">{error}</span> : null}
    </>
  );
}
